import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
} from "react";
import { useBeforeUnload, useParams } from "react-router";

import type { GrowthFormEditorState } from "./GrowthFormBuilderRoute";
import { useGrowthFormMutationState } from "./useGrowthFormMutationState";
import { useGrowthFormNavigationGuard } from "./useGrowthFormNavigationGuard";

import { useAuthState } from "@/auth/helloCashflowDomain/useAuthState";
import { useGetAdminV2MeQuery } from "@/store/api/baseApi";
import { VNextButton, VNextDialog } from "@/vnext/primitives";

export interface OwnedGrowthFormDraft {
  actorUid: string;
  authority: string;
  formId: string;
  organisationId: string | null;
  baseline: string;
  snapshot: string;
  value: GrowthFormEditorState;
}

interface GrowthFormDraftOwner {
  draft: OwnedGrowthFormDraft | null;
  setDraft: Dispatch<SetStateAction<OwnedGrowthFormDraft | null>>;
  retireOnClaimDenial: (actorUid: string) => void;
  mutation: ReturnType<typeof useGrowthFormMutationState>;
}

const Context = createContext<GrowthFormDraftOwner | null>(null);
export const useGrowthFormDraftOwner = () => useContext(Context);

function BeforeUnloadPrompt() {
  useBeforeUnload((event) => {
    event.preventDefault();
    event.returnValue = true;
  });
  return null;
}

function GrowthFormDraftProvider({ children }: { children: ReactNode }) {
  const { formId } = useParams<{ formId: string }>();
  const { user, loading } = useAuthState();
  const meQuery = useGetAdminV2MeQuery(undefined, {
    skip: loading || !user,
  });
  const me =
    !loading &&
    user &&
    !meQuery.isFetching &&
    !meQuery.isError &&
    meQuery.error === undefined &&
    meQuery.currentData?.staffUser.isActive &&
    meQuery.currentData.staffUser.firebaseUid === user.uid
      ? meQuery.currentData
      : null;
  const authority = me?.permissionKeys.includes("forms.manage")
    ? JSON.stringify([
        me.staffUser.firebaseUid,
        me.staffUser.id,
        [...me.permissionKeys].sort(),
      ])
    : null;
  const [draft, setDraft] = useState<OwnedGrowthFormDraft | null>(null);
  const retireOnClaimDenial = useCallback((actorUid: string) => {
    setDraft((current) => (current?.actorUid === actorUid ? null : current));
  }, []);
  const meError = meQuery.error;
  const meStatus =
    meError && typeof meError === "object" && "status" in meError
      ? meError.status
      : undefined;
  const refusedMe =
    !meQuery.isFetching &&
    meQuery.isError &&
    (meStatus === 401 || meStatus === 403 || meStatus === 404);
  const invalidMe =
    !loading &&
    !meQuery.isFetching &&
    !meQuery.isError &&
    meQuery.error === undefined &&
    Boolean(meQuery.currentData) &&
    !me;
  const dirty = Boolean(draft && draft.snapshot !== draft.baseline);
  // CRITICAL: the route gates can unmount their children during a reconnect.
  // Keep only a dirty draft above them, hidden until the same authority and
  // form are revalidated; refuse it after a real identity or access change.
  // https://redux.js.org/toolkit/rtk-query/api/setupListeners
  const retire = Boolean(
    draft &&
    ((user !== null && user.uid !== draft.actorUid) ||
      (!loading && !user) ||
      formId !== draft.formId ||
      (me !== null && authority !== draft.authority) ||
      invalidMe ||
      refusedMe ||
      (!dirty && !me)),
  );
  useLayoutEffect(() => {
    if (retire) setDraft(null);
  }, [retire]);
  const mutation = useGrowthFormMutationState();
  const {
    cancelPendingNavigation,
    confirmPendingNavigation,
    pendingNavigation,
    navigationBusy,
  } = useGrowthFormNavigationGuard({
    when: dirty && !retire,
    busy: mutation.busy,
    revision: draft?.snapshot ?? "",
  });

  return (
    <Context.Provider
      value={{
        draft: retire ? null : draft,
        setDraft,
        retireOnClaimDenial,
        mutation,
      }}
    >
      {children}
      {(dirty && !retire) || mutation.busy ? <BeforeUnloadPrompt /> : null}
      <VNextDialog
        description="Leave this form without saving the latest edits?"
        footer={
          <div className="vnext-inline-actions">
            <VNextButton
              disabled={navigationBusy}
              onClick={cancelPendingNavigation}
              tone="ghost"
            >
              Stay on this form
            </VNextButton>
            <VNextButton
              disabled={navigationBusy}
              onClick={confirmPendingNavigation}
              tone="primary"
            >
              Leave without saving
            </VNextButton>
          </div>
        }
        onClose={cancelPendingNavigation}
        open={Boolean(pendingNavigation)}
        title="Unsaved changes"
      >
        <p>
          Leave now to discard these edits, or stay and save the draft first.
        </p>
      </VNextDialog>
    </Context.Provider>
  );
}

/** Forms alone owns this state above the route's authentication gates. */
export function GrowthFormDraftBoundary({
  children,
  enabled = true,
}: {
  children: ReactNode;
  enabled?: boolean;
}) {
  return enabled ? (
    <GrowthFormDraftProvider>{children}</GrowthFormDraftProvider>
  ) : (
    <>{children}</>
  );
}
